import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Bot, KeyRound, Copy, Check, Send, PlayCircle, Loader2, Download, RefreshCw, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { OpenClawIdentities } from "./OpenClawIdentities";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const OC = `${API}/settings/integrations/openclaw`;
const cfg = { withCredentials: true };

const CopyButton = ({ value, testId }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <Button type="button" variant="outline" size="sm" onClick={copy} data-testid={testId}>
      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
    </Button>
  );
};

const StatusPill = ({ ok, label }) => (
  <Badge variant="outline" className={ok ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-slate-300 bg-slate-50 text-slate-500"}>
    {label}
  </Badge>
);

export const OpenClawSettings = () => {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({ webhook_url: "", hook_token: "", agent_id: "", channel: "telegram", chat_to: "", events: {} });
  const [deliveries, setDeliveries] = useState([]);
  const [busy, setBusy] = useState("");
  const [newKey, setNewKey] = useState(null);

  const load = useCallback(async () => {
    const [s, d] = await Promise.all([axios.get(OC, cfg), axios.get(`${OC}/deliveries`, cfg)]);
    setSettings(s.data);
    setDeliveries(d.data);
    setForm((f) => ({
      ...f,
      webhook_url: s.data.webhook_url || "",
      agent_id: s.data.agent_id || "",
      channel: s.data.channel || "telegram",
      chat_to: s.data.chat_to || "",
      events: s.data.events || {},
    }));
  }, []);

  useEffect(() => {
    load().catch(() => toast.error("Failed to load OpenClaw settings"));
  }, [load]);

  const run = async (name, fn, okMsg) => {
    setBusy(name);
    try {
      const res = await fn();
      if (okMsg) toast.success(typeof okMsg === "function" ? okMsg(res) : okMsg);
      await load();
      return res;
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Request failed");
    } finally {
      setBusy("");
    }
  };

  const toggleEnabled = (enabled) => run("enable", () => axios.put(OC, { enabled }, cfg), enabled ? "OpenClaw integration enabled" : "OpenClaw integration disabled");
  const save = () =>
    run("save", () => axios.put(OC, { ...form, hook_token: form.hook_token || undefined }, cfg), "Alert settings saved").then(() => setForm((f) => ({ ...f, hook_token: "" })));
  const generateKey = () =>
    run("key", async () => (await axios.post(`${OC}/generate-key`, {}, cfg)).data).then((d) => d && setNewKey(d.api_key));
  const revokeKey = () => run("revoke", () => axios.delete(`${OC}/key`, cfg), "Bot key revoked");
  const sendTest = () => run("test", () => axios.post(`${OC}/test`, {}, cfg), "Test alert delivered to OpenClaw");
  const runAlerts = () =>
    run("alerts", async () => (await axios.post(`${OC}/run-alerts`, {}, cfg)).data, (r) => `Checked: ${r.overdue_rentals} overdue, ${r.low_stock_parts} low-stock parts, ${r.depleted_rental_items} depleted items`);
  const downloadSkill = async () => {
    const res = await axios.get(`${OC}/skill`, { ...cfg, responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "SKILL.md";
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!settings) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  const base = process.env.REACT_APP_BACKEND_URL;
  const configSnippet = `{
  skills: {
    entries: {
      taskforge: {
        enabled: true,
        env: { TASKFORGE_BOT_KEY: "<paste bot key>" },
      },
    },
  },
  hooks: {
    enabled: true,
    token: "<same value as the Gateway hook token above>",
    allowedAgentIds: ["main"],
  },
  channels: { telegram: { botToken: "<telegram bot token>", allowFrom: ["<your telegram user id>"] } },
}`;

  return (
    <div className="grid gap-6" data-testid="openclaw-settings">
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2"><Bot className="h-5 w-5 text-indigo-600" /> OpenClaw chat bot</CardTitle>
              <CardDescription>Let staff query and create bookings and rentals from Telegram, and receive TaskForge alerts in chat.</CardDescription>
            </div>
            <div className="flex items-center gap-3">
              <Label htmlFor="oc-enabled" className="text-sm text-slate-600">{settings.enabled ? "Enabled" : "Disabled"}</Label>
              <Switch id="oc-enabled" checked={settings.enabled} disabled={busy === "enable"} onCheckedChange={toggleEnabled} data-testid="openclaw-enabled-switch" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          <StatusPill ok={settings.has_api_key} label={settings.has_api_key ? `Bot key ${settings.api_key_prefix}…` : "No bot key"} />
          <StatusPill ok={!!settings.webhook_url} label={settings.webhook_url ? "Gateway URL set" : "No gateway URL"} />
          <StatusPill ok={settings.last_delivery_status === "ok"} label={settings.last_delivery_at ? `Last alert ${settings.last_delivery_status} · ${new Date(settings.last_delivery_at).toLocaleString()}` : "No alerts sent yet"} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base"><KeyRound className="h-4 w-4" /> 1. Bot API key (OpenClaw → TaskForge)</CardTitle>
          <CardDescription>OpenClaw sends this key in the <code className="rounded bg-slate-100 px-1">X-Bot-Key</code> header. It works on the bot shortcuts and on every regular TaskForge API endpoint, with the access level chosen below.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="grid gap-1.5">
              <Label>Access level</Label>
              <div className="flex gap-2">
                {[["admin", "Admin — everything"], ["manager", "Manager — no users/settings"]].map(([v, l]) => (
                  <Button key={v} type="button" size="sm" variant={settings.bot_role === v ? "default" : "outline"} disabled={busy === "role"} onClick={() => run("role", () => axios.put(OC, { bot_role: v }, cfg), `Bot access set to ${v}`)} data-testid={`openclaw-role-${v}-btn`}>{l}</Button>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
              <div>
                <Label htmlFor="oc-deletes" className="text-sm">Allow permanent deletes</Label>
                <p className="text-xs text-slate-500">Off = the bot can cancel but never hard-delete records.</p>
              </div>
              <Switch id="oc-deletes" checked={!!settings.allow_deletes} disabled={busy === "deletes"} onCheckedChange={(v) => run("deletes", () => axios.put(OC, { allow_deletes: v }, cfg), v ? "Bot may now delete records" : "Bot deletes disabled")} data-testid="openclaw-allow-deletes-switch" />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
          <Button onClick={generateKey} disabled={busy === "key"} data-testid="openclaw-generate-key-btn">
            {busy === "key" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            {settings.has_api_key ? "Regenerate key" : "Generate key"}
          </Button>
          {settings.has_api_key && (
            <Button variant="outline" onClick={revokeKey} disabled={busy === "revoke"} data-testid="openclaw-revoke-key-btn">Revoke</Button>
          )}
          {settings.api_key_created_at && <span className="text-xs text-slate-500">Created {new Date(settings.api_key_created_at).toLocaleString()}</span>}
          <Button variant="ghost" onClick={downloadSkill} data-testid="openclaw-download-skill-btn"><Download className="mr-2 h-4 w-4" /> Download SKILL.md</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base"><Send className="h-4 w-4" /> 2. Alerts (TaskForge → OpenClaw)</CardTitle>
          <CardDescription>TaskForge posts alerts to your Gateway's <code className="rounded bg-slate-100 px-1">/hooks/agent</code> endpoint, which relays them to the chat below.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="grid gap-1.5 md:col-span-2">
              <Label htmlFor="oc-url">Gateway webhook URL</Label>
              <Input id="oc-url" placeholder="https://your-gateway.example.com/hooks/agent" value={form.webhook_url} onChange={(e) => setForm({ ...form, webhook_url: e.target.value })} data-testid="openclaw-webhook-url-input" />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="oc-token">Gateway hook token {settings.has_hook_token && <span className="text-xs text-slate-400">(saved — leave blank to keep)</span>}</Label>
              <Input id="oc-token" type="password" placeholder={settings.has_hook_token ? "••••••••" : "hooks.token from openclaw.json"} value={form.hook_token} onChange={(e) => setForm({ ...form, hook_token: e.target.value })} data-testid="openclaw-hook-token-input" />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="oc-agent">Agent ID <span className="text-xs text-slate-400">(optional)</span></Label>
              <Input id="oc-agent" placeholder="main" value={form.agent_id} onChange={(e) => setForm({ ...form, agent_id: e.target.value })} data-testid="openclaw-agent-id-input" />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="oc-channel">Channel</Label>
              <Input id="oc-channel" placeholder="telegram" value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })} data-testid="openclaw-channel-input" />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="oc-to">Deliver to (Telegram chat ID)</Label>
              <Input id="oc-to" placeholder="e.g. 123456789 or -100987654321 for a group" value={form.chat_to} onChange={(e) => setForm({ ...form, chat_to: e.target.value })} data-testid="openclaw-chat-to-input" />
            </div>
          </div>

          <div className="grid gap-2 rounded-lg border border-slate-200 p-4">
            <p className="text-sm font-medium text-slate-700">Send an alert when…</p>
            {Object.entries(settings.event_labels).map(([key, label]) => (
              <div key={key} className="flex items-center justify-between">
                <Label htmlFor={`ev-${key}`} className="text-sm font-normal text-slate-600">{label}</Label>
                <Switch id={`ev-${key}`} checked={form.events[key] !== false} onCheckedChange={(v) => setForm({ ...form, events: { ...form.events, [key]: v } })} data-testid={`openclaw-event-${key}-switch`} />
              </div>
            ))}
            <p className="text-xs text-slate-400">Digest alerts run daily at 07:00 SAST{settings.alerts_last_run_at ? ` · last run ${new Date(settings.alerts_last_run_at).toLocaleString()}` : ""}.</p>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button onClick={save} disabled={busy === "save"} data-testid="openclaw-save-btn">{busy === "save" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save alert settings</Button>
            <Button variant="outline" onClick={sendTest} disabled={busy === "test" || !settings.webhook_url || !settings.enabled} data-testid="openclaw-test-btn"><Send className="mr-2 h-4 w-4" />Send test alert</Button>
            <Button variant="outline" onClick={runAlerts} disabled={busy === "alerts"} data-testid="openclaw-run-alerts-btn"><PlayCircle className="mr-2 h-4 w-4" />Run digest now</Button>
          </div>
        </CardContent>
      </Card>

      <OpenClawIdentities
        requireActor={settings.require_actor}
        busy={busy === "actor"}
        onToggleRequire={(v) => run("actor", () => axios.put(OC, { require_actor: v }, cfg), v ? "Unlinked Telegram users are now blocked" : "Unlinked users get the bot's default access")}
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">5. Configure your OpenClaw Gateway</CardTitle>
          <CardDescription>On the machine running OpenClaw: download SKILL.md into <code className="rounded bg-slate-100 px-1">~/.openclaw/workspace/skills/taskforge/</code>, then merge this into <code className="rounded bg-slate-100 px-1">~/.openclaw/openclaw.json</code> and run <code className="rounded bg-slate-100 px-1">openclaw gateway restart</code>.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3">
          <div className="relative">
            <pre className="overflow-x-auto rounded-lg bg-slate-900 p-4 text-xs text-slate-100" data-testid="openclaw-config-snippet">{configSnippet}</pre>
            <div className="absolute right-2 top-2"><CopyButton value={configSnippet} testId="openclaw-copy-config-btn" /></div>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <span>TaskForge API base:</span>
            <code className="rounded bg-slate-100 px-1" data-testid="openclaw-base-url">{base}</code>
            <CopyButton value={base} testId="openclaw-copy-base-btn" />
          </div>
          <div className="flex items-start gap-2 rounded-md bg-amber-50 p-3 text-xs text-amber-800">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
            <span>Lock the Telegram channel to your staff's user IDs with <code>allowFrom</code>. Anyone who can message the bot acts with the access level above.</span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Recent alert deliveries</CardTitle></CardHeader>
        <CardContent>
          {deliveries.length === 0 ? (
            <p className="text-sm text-slate-500" data-testid="openclaw-no-deliveries">No alerts have been sent yet.</p>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="openclaw-deliveries-list">
              {deliveries.map((d) => (
                <li key={d.event_id} className="flex items-start justify-between gap-4 py-2 text-sm">
                  <div>
                    <p className="font-medium text-slate-800">{d.title}</p>
                    <p className="text-xs text-slate-500">{d.event} · {new Date(d.created_at).toLocaleString()}{d.error ? ` · ${d.error}` : ""}</p>
                  </div>
                  <StatusPill ok={d.status === "ok"} label={d.status} />
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!newKey} onOpenChange={(o) => !o && setNewKey(null)}>
        <DialogContent data-testid="openclaw-key-dialog">
          <DialogHeader>
            <DialogTitle>Your new bot API key</DialogTitle>
            <DialogDescription>Copy it now — it is shown only once. Paste it as <code>TASKFORGE_BOT_KEY</code> in your OpenClaw config.</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <code className="flex-1 break-all rounded-md bg-slate-100 p-3 text-xs" data-testid="openclaw-new-key">{newKey}</code>
            <CopyButton value={newKey || ""} testId="openclaw-copy-key-btn" />
          </div>
          <DialogFooter>
            <Button onClick={() => setNewKey(null)} data-testid="openclaw-key-dialog-close">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
